import { MutableRefObject, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { FixedSizeGrid, GridOnItemsRenderedProps } from 'react-window';
import { debounce } from '@/hooks/useDebounce';
import { useDimension } from '@/hooks/useDimension';

export const useRememberFixedSizeGridPosition = (
  scrollStartIndex: MutableRefObject<number>,
  columnCount: number
) => {
  const gridRef = useRef<FixedSizeGrid | null>(null);
  const isScrollableRef = useRef(false);

  useLayoutEffect(() => {
    const isScrollable = columnCount > 0;
    isScrollableRef.current = isScrollable;

    const grid = gridRef.current;
    if (isScrollable && grid && columnCount > 0) {
      grid.scrollToItem({
        align: 'start',
        rowIndex: Math.floor(scrollStartIndex.current / columnCount),
      });
    }
  }, [scrollStartIndex, columnCount]);

  const onItemsRendered = ({
    visibleColumnStartIndex,
    visibleRowStartIndex,
  }: GridOnItemsRenderedProps) => {
    if (isScrollableRef.current) {
      scrollStartIndex.current = visibleColumnStartIndex + visibleRowStartIndex * columnCount;
    }
  };

  return [gridRef, onItemsRendered] as const;
};

export const useRememberGridPosition = <RefType extends HTMLElement>(
  scrollStartIndex: MutableRefObject<number>,
  scrollDependencies: unknown[]
) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<RefType | null>(null);
  const tileOffsets = useRef<Map<number, number>>(new Map());

  // scrolls to the top if the dependencies have changed
  useLayoutEffect(() => {
    const scrollContainer = scrollRef.current;
    if (scrollContainer) {
      scrollContainer.scrollTop = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, scrollDependencies);

  // scrolls to the saved scrollStartIndex on the first render
  useLayoutEffect(() => {
    const index = scrollStartIndex.current;
    const grid = gridRef.current;
    if (grid && index > 0 && grid.children[index]) {
      grid.children[index].scrollIntoView({ block: 'start' });
    }
  }, [scrollStartIndex]);

  // saves the index and the top offset of every first column tile
  const [measureRef, dimension] = useDimension();
  useEffect(() => {
    const grid = gridRef.current;
    if (grid) {
      tileOffsets.current.clear();
      const children = grid.children;
      const count = children.length || 0;
      const columns = getComputedStyle(grid)
        .getPropertyValue('grid-template-columns')
        .split(' ').length;
      let index = 0;
      while (index < count) {
        tileOffsets.current.set(index, (children[index] as HTMLLIElement).offsetTop);
        index += columns;
      }
    }
  }, [dimension]);

  // adds a scroll listener to set the scroll position at the end of every scroll
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    const grid = gridRef.current;
    if (scrollContainer && grid) {
      const debouncedSetScrollIndex = debounce(() => {
        const scrollTop = scrollContainer.scrollTop;
        for (const [index, offset] of tileOffsets.current) {
          if (offset <= scrollTop) {
            scrollStartIndex.current = index;
          } else {
            break;
          }
        }
      }, 250);

      scrollContainer.addEventListener('scroll', debouncedSetScrollIndex);
      return () => scrollContainer.removeEventListener('scroll', debouncedSetScrollIndex);
    }
  }, [scrollStartIndex]);

  const combinedRef = useCallback(
    (ref: RefType | null) => {
      gridRef.current = ref;
      measureRef(ref);
    },
    [measureRef]
  );

  return [scrollRef, combinedRef] as const;
};
